import React from 'react';
import { useTheme } from '../../../context/ThemeContext';
import emptyBasket from "../../../images/empty_basket.svg";
import RadialChart from './RadialChart';
import '../../../css/scrollbar.css';

/**
 * Candlestick Tab Content - Shows candlestick patterns with radial chart
 */
const CandlestickTabContent = ({ data }) => {
  const { theme } = useTheme();

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <img src={emptyBasket} alt="No Data" className="w-[124px] h-[84px] mb-4" />
        <p className={`text-sm font-medium w-[188px] text-center ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
          No Data Available.
        </p>
      </div>
    );
  }

  const bullishCount = data.filter(item => item.type === 'Bullish').length;
  const bearishCount = data.filter(item => item.type === 'Bearish').length;
  const neutralCount = data.filter(item => item.type !== 'Bullish' && item.type !== 'Bearish').length;
  const total = data.length;
  const bullishPercentage = (bullishCount / total) * 100;

  return (
    <div className="flex h-full">
      {/* Left Column - Chart and Summary */}
      <div className="flex-[60] px-4">
        <h3 className="text-sm font-semibold mb-1 text-p300">
          Candlestick Pattern
        </h3>
        <p className={`text-xs font-medium mb-6 ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
          Following Candlestick patterns are identified.
        </p>

        {/* Radial Chart */}
        <div className="flex items-center justify-center mb-6">
          <RadialChart value={bullishPercentage} total={total} />
        </div>

        {/* Stats Row */}
        <div className="flex items-center justify-center gap-3 text-sm">
          <span className="font-normal text-p300">
            Bearish
          </span>
          <span className="font-semibold text-p300">
            {bearishCount}
          </span>
          <span className={`font-normal text-p300`}>
            • Neutral
          </span>
          <span className="font-semibold text-p300">
            {neutralCount}
          </span>
          <span className={`font-normal text-p300`}>
            • Bullish
          </span>
          <span className="font-semibold text-p300">
            {bullishCount}
          </span>
        </div>
      </div>

      {/* Divider */}
      <div className={`w-px ${theme === 'dark' ? 'bg-[#CFCFCF]' : 'bg-[#CFCFCF]'}`} />

      {/* Right Column - List of Patterns */}
      <div
        className="flex-[40] overflow-y-auto custom-scrollbar"
        data-theme={theme}
      >
        {data.map((item, index) => (
          <div
            key={index}
            className="flex items-center gap-3 px-4 py-4 "
            style={{ minHeight: '80px' }}
          >
            {/* SVG Icon - Using placeholder for now */}
            <div className={`w-16 h-16 flex-shrink-0 rounded-md flex items-center justify-center ${theme === 'dark' ? 'bg-[#1F1F1F]' : 'bg-[#F5F5F5]'}`}>
              {item.image ? (
                <img src={item.image} alt={item.name} className="w-12 h-12" />
              ) : (
                <div className={`text-2xl ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'}`}>
                  📊
                </div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#EDEDED]' : 'text-[#272727]'}`}>
                  {item.name}
                </span>
                {item.timeframe && (
                  <span className="px-2 py-0.5 rounded-sm text-[10px] font-medium bg-blue-shade text-blue">
                    {item.timeframe}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span
                  className={`font-normal ${item.tags?.[0]?.tag_name === 'Bearish'
                    ? 'text-red'
                    : 'text-p150'
                    }`}
                >
                  {item.tags?.[0]?.tag_name || ''}
                </span>
                {item.tags?.[1] && (
                  <>
                    <span className={`text-n500`}>•</span>
                    <span className={`font-normal text-n500`}>
                      {item.tags[1]?.tag_name || ''}
                    </span>
                  </>
                )}
                {item.tags?.[2] && (
                  <>
                    <span className={`text-n500`}>•</span>
                    <span className={`font-normal text-n500`}>
                      {item.tags[2]?.tag_name || ''}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CandlestickTabContent;